/**
 * The frame every staff screen sits in.
 *
 * Navigation is scoped to the event being worked on, because that is how the day actually runs:
 * someone is at the desk, or on the gate, or tabulating — not browsing a product. The nav shows
 * only what the person's permissions allow, so nobody is offered a menu item that will fail on
 * click (§24).
 */

import {
  NavLink,
  Outlet,
  ScrollRestoration,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { Suspense, useEffect, useMemo } from "react";
import { Button, Skeleton, cn } from "@powwow/ui";
import type { Permission } from "@powwow/validation";
import { SessionProvider, useSession } from "./session.js";
import { TranslationProvider, useT } from "./i18n.js";
import { api, brandImageSrc, signOut } from "./api.js";
import { useAsync } from "./use-async.js";
import { armedScanner, stationPath, STATION_NAMES } from "./lib/scan-mode.js";
import { PlatformFooter } from "./platform-footer.js";

export function AppShell() {
  return (
    <SessionProvider>
      <TranslationProvider>
        <Shell />
      </TranslationProvider>
    </SessionProvider>
  );
}

interface NavItem {
  to: string;
  label: string;
  /** Absent means everyone with a session sees it — your own profile, and nothing else. */
  permission?: Permission | undefined;
}

/** One nav group, rendered as a list of links the person is actually allowed to follow. */
function NavGroup({
  title,
  items,
  permissions,
  eventId,
}: {
  title: string;
  items: NavItem[];
  permissions: string[];
  eventId: string | undefined;
}) {
  const allowed = items.filter((item) => !item.permission || permissions.includes(item.permission));
  if (allowed.length === 0) return null;

  return (
    <div className="mb-6">
      <p className="mb-3 text-caption font-black tracking-wide text-muted uppercase">{title}</p>
      <ul className="space-y-1">
        {allowed.map((item) => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              end={item.to === `/events/${eventId}` || item.to === "/events"}
              className={({ isActive }) =>
                cn(
                  "flex min-h-touch items-center rounded-md px-3 font-bold transition",
                  isActive
                    ? "raised bg-brand text-brand-contrast shadow-lg"
                    : "text-muted hover:bg-accent-surface/70 hover:text-accent",
                )
              }
            >
              {item.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Shell() {
  const { me, loading, community, setCommunity, impersonating, exitImpersonation } = useSession();
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const { eventId } = useParams();

  /**
   * An armed scanner on a screen that is not listening to it.
   *
   * Scan mode outlives the check-in screen now, which is what a shared gate machine needs — and it
   * creates the one failure this app cannot let happen quietly: somebody walks up to a laptop
   * showing Results, scans four people, hears nothing, and assumes it worked. Nothing on the screen
   * would have said otherwise, because the screen listening for scans is a different one.
   *
   * Read at render rather than subscribed to: this recomputes on every navigation, which is exactly
   * when the answer can change while this rail is the thing that shows it.
   */
  /**
   * The community's mark, as *this* app has to request it.
   *
   * `/v1/auth/me` sends the value as stored, which for an upload is a `/media/{id}` path. That path
   * belongs to the public site's proxy, not here: this app is static assets with a single-page
   * fallback, so `/media/{id}` on its own origin returns `index.html` and the mark renders as a
   * broken image. `brandImageSrc` points it at the API, and returns nothing for a value that is
   * neither an upload nor a safe link — so a damaged setting falls back to the platform icon
   * instead of a broken box.
   */
  const communityLogo = brandImageSrc(community?.logo);

  const scannerElsewhere = useMemo(() => {
    const armed = armedScanner();
    return armed && location.pathname !== stationPath(armed) ? armed : null;
  }, [location.pathname]);

  useEffect(() => {
    if (loading) return;
    if (!me?.authenticated) {
      navigate("/sign-in", { replace: true, state: { from: location.pathname } });
      return;
    }
    // A password somebody else chose opens exactly one door. The server already refuses to let it
    // stay (it is single-use and expiring); this is what stops someone wandering the app with it.
    if (me.mustPasswordReset) navigate("/set-password", { replace: true });
  }, [loading, me, navigate, location.pathname]);

  if (loading) {
    return (
      <div className="mx-auto max-w-5xl p-6">
        <Skeleton rows={4} />
      </div>
    );
  }

  if (!me?.authenticated) return null;

  if (!community) {
    const hasMany = me.communities.length > 1;
    return (
      <main className="mx-auto max-w-2xl p-6">
        {me.communities.length > 0 ? (
          <>
            <h1 className="mb-2 text-heading font-bold">
              {hasMany ? "Choose a community" : "Your community"}
            </h1>
            <p className="mb-4 text-muted">
              {hasMany
                ? "You belong to more than one. Everything you do is recorded against the one you pick."
                : "Everything you do is recorded against the community you pick."}
            </p>
            <ul className="space-y-2">
              {me.communities.map((membership) => (
                <li key={membership.id}>
                  <Button className="w-full justify-start" onClick={() => setCommunity(membership)}>
                    {membership.name}
                  </Button>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <>
            <h1 className="mb-2 text-heading font-bold">
              {me.platformAdmin ? "Platform administration" : "No community yet"}
            </h1>
            <p className="mb-4 text-muted">
              {me.platformAdmin
                ? "You are not a member of any community. As a platform administrator you can create one — its owner runs it from there."
                : "You are not a member of any community yet. When someone invites you, the invitation link brings you in."}
            </p>
          </>
        )}
        {me.platformAdmin && (
          <div className="mt-6 border-t border-line pt-4">
            <Button onClick={() => navigate("/admin/communities")}>
              Open the platform console
            </Button>
          </div>
        )}
      </main>
    );
  }

  /**
   * The event workspace.
   *
   * Labels resolve through the community's terminology before falling back to a default, so a
   * community that calls a payout an honorarium reads "Honoraria" in its own navigation. The
   * translator is built from the signed-in person's locale and the community's supported set, and
   * the words are the community's regardless of which language they read (ux.md §2).
   */
  const items: NavItem[] = eventId
    ? [
        { to: `/events/${eventId}`, label: "Overview", permission: "event.read" },
        {
          to: `/events/${eventId}/categories`,
          label: "Categories",
          permission: "competition.manage",
        },
        {
          to: `/events/${eventId}/desk`,
          label: `${t.Term("registration")} desk`,
          permission: "registration.create",
        },
        {
          to: `/events/${eventId}/check-in`,
          label: t.Term("checkIn"),
          permission: "checkin.record",
        },
        { to: `/events/${eventId}/judges`, label: t.Term("judge", 2), permission: "judge.assign" },
        { to: `/events/${eventId}/teams`, label: "Teams", permission: "team.manage" },
        // Everyone at the event who is not competing — MC, arena director, drums, elders, crew
        // (D62). Under `event.manage`, which is the permission that already covered the roster.
        { to: `/events/${eventId}/roles`, label: "Roster", permission: "event.manage" },
        { to: `/events/${eventId}/results`, label: "Results", permission: "result.read" },
        { to: `/events/${eventId}/prizes`, label: t.Term("prize", 2), permission: "prize.manage" },
        { to: `/events/${eventId}/payouts`, label: t.Term("payout", 2), permission: "payout.read" },
        { to: `/events/${eventId}/updates`, label: "Updates", permission: "announcement.manage" },
        { to: `/events/${eventId}/qr-codes`, label: "QR codes", permission: "event.read" },
      ]
    : [];

  /**
   * The community-wide screens.
   *
   * Always present, because they are not about the event someone happens to be inside: a form is
   * reused across years, a member's role outlives any one weekend, and the terminology is the
   * community's rather than the event's.
   */
  const communityItems: NavItem[] = [
    { to: "/dashboard", label: "Today" },
    { to: "/events", label: t.Term("event", 2), permission: "event.read" },
    { to: "/participants", label: t.Term("participant", 2), permission: "registration.read" },
    { to: "/forms", label: "Forms", permission: "form.manage" },
    { to: "/surveys", label: "Surveys", permission: "survey.manage" },
    { to: "/analytics", label: "Analytics", permission: "analytics.read" },
    { to: "/exports", label: "Exports", permission: "export.create" },
    { to: "/members", label: "Members", permission: "community.members.manage" },
    { to: "/audit", label: "Audit log", permission: "community.audit.read" },
    { to: "/settings", label: "Settings", permission: "community.manage" },
    { to: "/profile", label: "Your profile" },
    // No permission: help is for everyone signed in, like the profile link above it.
    { to: "/help", label: "Help" },
  ];

  const mobileItems = items.length > 0 ? items : communityItems;

  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[17rem_minmax(0,1fr)]">
      {/* Reset the window to the top on every navigation, and restore it on back/forward. Without
          this a click from halfway down one screen opens the next one already scrolled — most
          visible moving between help topics, but true of every link. The page scrolls the window,
          not an inner pane, so the default window restoration is the right one. */}
      <ScrollRestoration />
      {/* The rail reads as a slightly different plane from the content beside it: paper at the top
          fading into the aqua surface at the bottom, and a hairline rather than a border-weight
          rule. It is the cheapest way to make a 17rem column look built rather than left over. */}
      <aside
        data-print="hide"
        className="hidden border-r border-line/70 bg-gradient-to-b from-paper via-paper to-sunk/60 p-5 lg:sticky lg:top-0 lg:flex lg:h-dvh lg:flex-col"
      >
        <NavLink to="/events" className="mb-8 flex items-center gap-3">
          <img src="/brand/icon-mark.png" alt="" className="size-11 shrink-0" />
          <span className="min-w-0">
            <span className="block text-body-large font-black leading-tight">PowWow</span>
            <span className="block truncate text-caption font-semibold text-muted">
              {community.name}
            </span>
          </span>
        </NavLink>

        <nav aria-label="Main" className="min-h-0 flex-1 overflow-y-auto">
          <NavGroup
            title="Event workspace"
            items={items}
            permissions={community.permissions}
            eventId={eventId}
          />
          <NavGroup
            title="Community"
            items={communityItems}
            permissions={community.permissions}
            eventId={eventId}
          />
          {me.platformAdmin && (
            <NavGroup
              title="Platform"
              items={[{ to: "/admin/communities", label: "Communities" }]}
              permissions={community.permissions}
              eventId={eventId}
            />
          )}
        </nav>

        <div className="mt-6 border-t border-line pt-4">
          <p className="text-caption font-semibold text-muted">Signed in as</p>
          <p className="truncate font-bold">{me.user.name}</p>
          <Button
            className="mt-3 w-full"
            variant="secondary"
            onClick={async () => {
              await signOut();
              navigate("/sign-in", { replace: true });
            }}
          >
            Sign out
          </Button>
        </div>
      </aside>

      <div className="min-w-0">
        {/* The header and the phone's event tabs stick as one block rather than each on its own.
            Sticking them separately means guessing the header's height in the tab bar's `top`, and
            that height is a guess that changes — the header wraps to two rows on a narrow phone
            with a long community name, and the tabs would then sit over it. */}
        <div data-print="hide" className="glass sticky top-0 z-20 border-b border-line/70">
          {/* The impersonation rail (D48). It rides inside the sticky header block — the same way
              the mobile event tabs do — so a platform admin can never lose sight of whose workspace
              they are in, or the way out, however far they scroll. */}
          {impersonating && (
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-warning/30 bg-warning-surface px-4 py-2 text-warning lg:px-8">
              <p className="min-w-0 text-caption font-bold">
                Platform view — you are operating{" "}
                <span className="font-black">{community.name}</span> as an administrator. Everything
                you do here is recorded against you.
              </p>
              <Button
                variant="secondary"
                onClick={() => {
                  exitImpersonation();
                  navigate("/admin/communities", { replace: true });
                }}
              >
                Exit to console
              </Button>
            </div>
          )}
          {/* The armed-scanner rail. Same reasoning as the one above it, for the same reason: the
              state is real and invisible, and the person who has to know is not the one who set
              it. */}
          {scannerElsewhere && (
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-accent/30 bg-accent-surface px-4 py-2 text-accent lg:px-8">
              <p className="min-w-0 text-caption font-bold">
                The scanner is on for{" "}
                <span className="font-black">{scannerElsewhere.eventName || "this event"}</span> —
                scans only count on the {STATION_NAMES[scannerElsewhere.station]} screen. Anything
                scanned here is typed as text.
              </p>
              <Button variant="secondary" onClick={() => navigate(stationPath(scannerElsewhere))}>
                {scannerElsewhere.station === "payout" ? "Back to payouts" : "Back to the gate"}
              </Button>
            </div>
          )}
          <header className="px-4 py-3 lg:px-8">
            <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4">
              {/* Whose event this is comes first, and the software's name second. A community that
                  has set a mark gets it at every width — this is the one place in the frame that
                  says which arena you are standing in, and on a shared device that is the thing
                  worth being unmistakable. Without a mark the platform icon stands in, and only on
                  a phone: the desktop rail above the nav already carries it. */}
              <div className="flex min-w-0 items-center gap-3">
                <NavLink
                  to="/events"
                  className={cn("shrink-0", !communityLogo && "lg:hidden")}
                  aria-label={communityLogo ? `${community.name} events` : "PowWow events"}
                >
                  <img
                    src={communityLogo ?? "/brand/icon-mark.png"}
                    alt=""
                    /* `object-contain`, because a community's mark is whatever shape they sent —
                       a wordmark would be cropped by anything that fills the box. */
                    className="size-10 object-contain"
                  />
                </NavLink>
                <div className="min-w-0">
                  <p className="truncate text-body-large font-black leading-tight">
                    {community.name}
                  </p>
                  <p className="truncate text-caption font-semibold text-muted">
                    PowWow operations
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <NotificationBell communityId={community.id} />
                <NavLink
                  to="/profile"
                  className="hidden max-w-48 truncate text-caption font-bold text-muted no-underline hover:text-accent hover:underline sm:block"
                >
                  {me.user.name}
                </NavLink>
                <Button
                  variant="secondary"
                  onClick={async () => {
                    await signOut();
                    navigate("/sign-in", { replace: true });
                  }}
                >
                  Sign out
                </Button>
              </div>
            </div>
          </header>

          {mobileItems.length > 0 && (
            <nav
              aria-label={items.length > 0 ? "This event" : "Community"}
              className="border-t border-line/60 lg:hidden"
            >
              <ul className="flex gap-2 overflow-x-auto px-4 py-3">
                {mobileItems
                  .filter(
                    (item) => !item.permission || community.permissions.includes(item.permission),
                  )
                  .map((item) => (
                    <li key={item.to} className="shrink-0">
                      <NavLink
                        to={item.to}
                        end={item.to === `/events/${eventId}` || item.to === "/events"}
                        className={({ isActive }) =>
                          cn(
                            "inline-flex min-h-touch items-center rounded-md border px-4 font-bold transition",
                            isActive
                              ? "raised border-brand bg-brand text-brand-contrast shadow-md"
                              : "border-line bg-paper text-muted shadow-sm hover:border-line-strong hover:bg-accent-surface hover:text-accent",
                          )
                        }
                      >
                        {item.label}
                      </NavLink>
                    </li>
                  ))}
              </ul>
            </nav>
          )}
        </div>

        {/* Every screen below is a lazily loaded chunk, so an arena device that only ever opens
            check-in never downloads the analytics, audit or form-builder code. The fallback is the
            same Skeleton a slow load already shows, which is why arriving at a screen looks the
            same whether the chunk was cached or not. */}
        <main className="mx-auto max-w-6xl px-4 py-8 pb-16 lg:px-8">
          <Suspense fallback={<Skeleton rows={5} />}>
            <Outlet />
          </Suspense>
        </main>
        <PlatformFooter className="border-t border-line/70" />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ notifications */

/**
 * The unread count, in the header of every screen.
 *
 * A count and a word, never a bare dot: "3 unread notifications" is what a screen reader announces
 * and what a person reads in the sun, and a coloured dot is the first thing both of them lose.
 *
 * Polled rather than pushed, at a minute — the rules that write these run on demand, a person is
 * not waiting on the second, and a socket held open across an afternoon on venue wifi is a cost
 * with no matching benefit.
 */
function NotificationBell({ communityId }: { communityId: string }) {
  const notifications = useAsync(
    () => api.notifications(communityId, { unreadOnly: true }).catch(() => ({ notifications: [] })),
    [communityId],
  );

  useEffect(() => {
    const timer = setInterval(() => notifications.reload(), 60_000);
    return () => clearInterval(timer);
    // `reload` is stable for the life of the hook; re-running this on every render would reset the
    // interval before it ever fired.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const unread = notifications.data?.notifications.length ?? 0;

  return (
    <NavLink
      to="/notifications"
      className={cn(
        "inline-flex min-h-touch items-center gap-2 rounded-md border px-3 font-bold no-underline transition",
        unread > 0
          ? "border-brand bg-accent-surface text-accent"
          : "border-line bg-paper text-muted hover:bg-sunk",
      )}
    >
      {/* A bell drawn inline, so no icon font travels over venue wifi. */}
      <svg aria-hidden="true" viewBox="0 0 20 20" className="size-5" fill="currentColor">
        <path d="M10 2a5 5 0 0 0-5 5v3.2L3.6 13a.8.8 0 0 0 .7 1.2h11.4a.8.8 0 0 0 .7-1.2L15 10.2V7a5 5 0 0 0-5-5Zm0 15.5a2.2 2.2 0 0 0 2.1-1.6H7.9A2.2 2.2 0 0 0 10 17.5Z" />
      </svg>
      <span className="tabular">{unread}</span>
      <span className="sr-only">
        {unread === 0
          ? "No unread notifications"
          : `${unread} unread ${unread === 1 ? "notification" : "notifications"}`}
      </span>
    </NavLink>
  );
}
